import React, { useState } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import { NumericFormat } from "react-number-format";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { useUserData } from "../../hooks";

export default function SubscriptionCreditsTable() {
    const {
        lifeTimeTotal,
        lifetimeCredit,
        availableCredit,
        userReferralCode,
        groupReferralCode,
    } = useUserData();

    const [copyTooltip, setCopyTooltip] = useState(false);
    const referralCode = groupReferralCode ? groupReferralCode : userReferralCode;
    const signupUrl = `${window.location.origin}/signup?referral_code=${referralCode}`;

    const handleCopyReferral = () => {
        navigator.clipboard
            .writeText(signupUrl)
            .then(() => {
                setCopyTooltip(true);
            })
            .catch((error) => {
                console.error("Unable to copy data to clipboard:", error);
            });
    };

    const handleMouseOut = () => {
        setTimeout(() => {
            setCopyTooltip(false);
        }, 1000);
    };

    const creditsRows = [
        {
            id: "referrals",
            name: "Referrals",
            lifeTimeTotal,
            lifetimeCredit,
            availableCredit,
            referralCode,
        },
    ];

    const creditsColumns = [
        {
            field: "name",
            headerName: "",
            flex: 2,
            renderCell: ({ value }) => <strong>{value}</strong>,
        },
        {
            field: "lifeTimeTotal",
            headerName: "Lifetime Total",
            flex: 2,
            headerAlign: "center",
            align: "center",
        },
        {
            field: "lifetimeCredit",
            headerName: "Lifetime Credit",
            flex: 2,
            headerAlign: "center",
            align: "center",
            renderCell: ({ value }) => (
                <NumericFormat
                    value={value}
                    decimalScale={2}
                    prefix="$"
                    defaultValue="-"
                    displayType="text"
                    fixedDecimalScale={true}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                />
            ),
        },
        {
            field: "availableCredit",
            headerName: "Available Credit",
            flex: 2,
            headerAlign: "center",
            align: "center",
            renderCell: ({ value }) => (
                <NumericFormat
                    value={value}
                    decimalScale={2}
                    prefix="$"
                    defaultValue="-"
                    displayType="text"
                    fixedDecimalScale={true}
                    thousandSeparator=","
                    thousandsGroupStyle="thousand"
                />
            ),
        },
        {
            field: "referralCode",
            headerName: "",
            flex: 3,
            headerAlign: "center",
            align: "center",
            renderCell: ({ value }) => (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {value}
                    {value && (
                        <Tooltip
                            title={
                                copyTooltip ? "Copied to clipboard!" : "Copy referral link"
                            }
                        >
                            <IconButton
                                onClick={handleCopyReferral}
                                size="small"
                                onMouseOut={handleMouseOut}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            ),
        },
    ];

    return (
        <Box sx={{ pb: 8 }}>
            <Typography variant="h5" gutterBottom sx={{ mt: 4 }}>
                Subscription Credits
            </Typography>
            <DataGrid
                columns={creditsColumns}
                rows={creditsRows}
                density="compact"
                autoHeight
                hideFooter
                disableColumnMenu
                disableRowSelectionOnClick
                sx={{
                    border: "none",
                    maxWidth: "100vw",
                    [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
                        outline: "none",
                    },
                    [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]: {
                        outline: "none",
                    },
                }}
            />
        </Box>
    );
}
